import {
  AppShell as MantineAppShell,
  Burger,
  Group,
  NavLink,
  ScrollArea,
  Text,
  ActionIcon,
  Tooltip,
  UnstyledButton,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconMoon,
  IconSun,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { navItems } from '../routes/registry';
import { useAuth } from '../auth/AuthProvider';

const NAVBAR_WIDTH_OPEN = 240;
const NAVBAR_WIDTH_COLLAPSED = 68;

export function AppShell({ children }: { children: ReactNode }) {
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure();
  const [desktopCollapsed, { toggle: toggleDesktop }] = useDisclosure(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();

  const isActive = (path: string): boolean => {
    if (path === '/') return location.pathname === '/';
    const root = path.replace(/\/\*$/, '');
    return location.pathname === root || location.pathname.startsWith(root + '/');
  };

  return (
    <MantineAppShell
      header={{ height: 56 }}
      navbar={{
        width: desktopCollapsed ? NAVBAR_WIDTH_COLLAPSED : NAVBAR_WIDTH_OPEN,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened },
      }}
      padding="md"
    >
      <MantineAppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
            <Tooltip label={desktopCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              <ActionIcon
                variant="subtle"
                onClick={toggleDesktop}
                visibleFrom="sm"
                size="lg"
                aria-label="Toggle sidebar"
              >
                {desktopCollapsed ? (
                  <IconLayoutSidebarLeftExpand size={18} />
                ) : (
                  <IconLayoutSidebarLeftCollapse size={18} />
                )}
              </ActionIcon>
            </Tooltip>
            <UnstyledButton onClick={() => navigate('/')} aria-label="Home">
              <Group gap="xs" wrap="nowrap">
                <img src="/logo.png" alt="" width={28} height={28} />
                <Text fw={600} size="md" visibleFrom="xs">
                  Morrell Dev
                </Text>
              </Group>
            </UnstyledButton>
          </Group>
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Toggle color scheme">
              <ActionIcon variant="subtle" onClick={() => toggleColorScheme()} size="lg">
                {colorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
              </ActionIcon>
            </Tooltip>
            <Text size="sm" c="dimmed" visibleFrom="md">
              {user?.email}
            </Text>
          </Group>
        </Group>
      </MantineAppShell.Header>

      <MantineAppShell.Navbar p="xs">
        <MantineAppShell.Section grow component={ScrollArea}>
          {navItems.map((item) => {
            const target = item.path.replace(/\/\*$/, '') || '/';
            const active = isActive(item.path);
            const icon = <item.Icon size={20} stroke={1.6} />;

            if (desktopCollapsed) {
              return (
                <Tooltip key={item.key} label={item.label} position="right" withArrow>
                  <ActionIcon
                    variant={active ? 'light' : 'subtle'}
                    color={active ? 'mdev' : 'gray'}
                    size="xl"
                    onClick={() => {
                      navigate(target);
                      closeMobile();
                    }}
                    aria-label={item.label}
                    style={{ width: '100%', marginBottom: 4 }}
                  >
                    {icon}
                  </ActionIcon>
                </Tooltip>
              );
            }

            return (
              <NavLink
                key={item.key}
                label={item.label}
                leftSection={icon}
                active={active}
                onClick={() => {
                  navigate(target);
                  closeMobile();
                }}
              />
            );
          })}
        </MantineAppShell.Section>
      </MantineAppShell.Navbar>

      <MantineAppShell.Main>{children}</MantineAppShell.Main>
    </MantineAppShell>
  );
}
